import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  Timestamp,
} from 'firebase/firestore'
import type { User } from 'firebase/auth'
import type { InviteDoc, Role } from '@racedash/shared'
import { db } from '../lib/firebase'

const INVITE_VALID_DAYS = 14

/** Same construction as a share token: the invite is reachable only by holding
 *  it, so it has to be unguessable rather than merely unique. */
function newToken(): string {
  const bytes = new Uint8Array(24)
  crypto.getRandomValues(bytes)
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

export async function createInvite(
  orgId: string,
  orgName: string,
  email: string,
  role: Role,
  uid: string,
): Promise<string> {
  const token = newToken()
  const expires = Timestamp.fromMillis(Date.now() + INVITE_VALID_DAYS * 86_400_000)
  await setDoc(doc(collection(db, 'invites'), token), {
    orgId,
    orgName,
    email: email.trim(),
    role,
    createdBy: uid,
    createdAt: serverTimestamp(),
    expiresAt: expires,
    acceptedBy: null,
    acceptedAt: null,
  })
  return token
}

export async function revokeInvite(token: string): Promise<void> {
  await deleteDoc(doc(db, 'invites', token))
}

export function inviteUrl(token: string): string {
  return `${window.location.origin}/join/${token}`
}

export async function readInvite(token: string): Promise<InviteDoc | null> {
  const snap = await getDoc(doc(db, 'invites', token))
  return snap.exists() ? (snap.data() as InviteDoc) : null
}

export type AcceptResult =
  | { ok: true; orgId: string }
  | { ok: false; reason: 'missing' | 'used' | 'expired' | 'already-member' | 'failed' }

/**
 * Claims an invite and creates the membership in a single atomic batch.
 *
 * Both halves must land together: the security rule reads the invite's
 * post-commit state, so a token cannot be replayed and an already-claimed one
 * is refused. Splitting these would leave a window where a membership exists
 * that no invite ever authorised.
 */
export async function acceptInvite(token: string, user: User): Promise<AcceptResult> {
  const invite = await readInvite(token)
  if (!invite) return { ok: false, reason: 'missing' }
  if (invite.acceptedBy) return { ok: false, reason: 'used' }
  if (invite.expiresAt.toMillis() <= Date.now()) return { ok: false, reason: 'expired' }

  const memberRef = doc(db, 'orgs', invite.orgId, 'members', user.uid)
  if ((await getDoc(memberRef)).exists()) {
    return { ok: false, reason: 'already-member' }
  }

  const batch = writeBatch(db)
  batch.update(doc(db, 'invites', token), {
    acceptedBy: user.uid,
    acceptedAt: serverTimestamp(),
  })
  batch.set(memberRef, {
    uid: user.uid,
    role: invite.role,
    displayName: user.displayName,
    email: user.email,
    inviteToken: token,
    joinedAt: serverTimestamp(),
  })

  try {
    await batch.commit()
    return { ok: true, orgId: invite.orgId }
  } catch {
    return { ok: false, reason: 'failed' }
  }
}

export async function setMemberRole(orgId: string, uid: string, role: Role): Promise<void> {
  await updateDoc(doc(db, 'orgs', orgId, 'members', uid), { role })
}

export async function removeMember(orgId: string, uid: string): Promise<void> {
  await deleteDoc(doc(db, 'orgs', orgId, 'members', uid))
}
